export type WizardStep = 'identity' | 'code' | 'password' | 'welcome' | 'profile' | 'intro';

export interface WizardState {
  step: WizardStep;
  token: string;
  email: string;
  username: string;
  /** Set only after this browser successfully clicked 继续 (startRegister). */
  continued: boolean;
}

export const WIZARD_KEY = 'mgr.register.wizard';
/** One-shot: first-time signup must still see 个人简介 after page 1 creates the user. */
export const INTRO_PENDING_KEY = 'mgr.register.introPending';

const STEPS = new Set<WizardStep>(['identity', 'code', 'password', 'welcome', 'profile', 'intro']);

export function emptyWizard(): WizardState {
  return { step: 'identity', token: '', email: '', username: '', continued: false };
}

function isStep(value: unknown): value is WizardStep {
  return typeof value === 'string' && STEPS.has(value as WizardStep);
}

function needsToken(step: WizardStep): boolean {
  return step === 'password' || step === 'welcome' || step === 'profile';
}

/**
 * Resume later steps only for a valid in-progress flow from this browser
 * after 继续: continued flag + username + email. Stale leftovers (old
 * overlays, "已有验证码？", another email's pending) fall back to step 1.
 */
export function sanitizeWizard(parsed: Partial<WizardState> | null | undefined): WizardState {
  if (!parsed) return emptyWizard();
  const username = typeof parsed.username === 'string' ? parsed.username.trim() : '';
  const email = typeof parsed.email === 'string' ? parsed.email.trim() : '';
  const token = typeof parsed.token === 'string' ? parsed.token : '';
  const continued = parsed.continued === true;
  const step = isStep(parsed.step) ? parsed.step : 'identity';

  if (
    !continued ||
    step === 'identity' ||
    username.length === 0 ||
    email.length === 0 ||
    (needsToken(step) && token.length === 0)
  ) {
    return emptyWizard();
  }

  return { step, token, email, username, continued: true };
}

export function isIntroPending(): boolean {
  try {
    return sessionStorage.getItem(INTRO_PENDING_KEY) === '1';
  } catch {
    return false;
  }
}

export function markIntroPending() {
  sessionStorage.setItem(INTRO_PENDING_KEY, '1');
}

export function clearIntroPending() {
  sessionStorage.removeItem(INTRO_PENDING_KEY);
}

function introPendingState(base: WizardState): WizardState {
  return {
    step: 'intro',
    token: base.token,
    email: base.email,
    username: base.username,
    continued: true,
  };
}

export function loadWizard(): WizardState {
  try {
    const raw = sessionStorage.getItem(WIZARD_KEY);
    const parsed = raw ? (JSON.parse(raw) as Partial<WizardState>) : null;
    const next = sanitizeWizard(parsed);
    if (isIntroPending()) return introPendingState(next);
    if (!next.continued) sessionStorage.removeItem(WIZARD_KEY);
    return next;
  } catch {
    sessionStorage.removeItem(WIZARD_KEY);
    if (isIntroPending()) return introPendingState(emptyWizard());
    return emptyWizard();
  }
}

export function saveWizard(state: WizardState) {
  if (isIntroPending()) {
    sessionStorage.setItem(WIZARD_KEY, JSON.stringify(introPendingState(state)));
    return;
  }
  if (!state.continued || state.step === 'identity') {
    sessionStorage.removeItem(WIZARD_KEY);
    return;
  }
  sessionStorage.setItem(WIZARD_KEY, JSON.stringify(state));
}

export function clearWizard() {
  sessionStorage.removeItem(WIZARD_KEY);
  clearIntroPending();
}
